/**
 * org-from-source — the source-backed editable starter.
 *
 * When a folder has canonical people records, a NEW org chart should start
 * from that data, not from a blank canvas: fetch the permissioned org model
 * (per-viewer masking applies server-side), map it to the universal graph,
 * and materialize ordinary EDITABLE nodes through the same hydrator +
 * command path the CSV import uses. Nodes carry `attrs.folderRecordId`, so
 * record linking and data hydration keep working.
 *
 * This is deliberately NOT the removed worker-side `seedFromFolderData`
 * path: it seeds from canonical records (never a sibling Y.Doc), it is
 * explicit (user picks the starter), and it mutates the canvas only via
 * CommandManager. See
 * docs/plans/2026-08-03-feat-atlas-planning-diagrams-and-live-views-plan.md.
 */

import type * as Y from 'yjs';
import type { CommandManager } from '../../../core/commands/CommandManager';
import type { SeedProvider } from '../../../core/seeds/SeedRegistry';
import type { OrgUniversalGraph } from '../interop/OrgChartHydrator';
import {
  DEFAULT_ORG_CHART_VIEW_MODE,
  DEFAULT_ORG_PERSON_NODE_TYPE,
} from '../models/defaults';
import type { OrgModelProjection } from '../models/OrgModelProjection';
import {
  buildPeopleHierarchy,
  departmentMembership,
} from '../services/buildPeopleChartScene';

type RequestFn = (input: string, init?: RequestInit) => Promise<Response>;

const personNodeId = (personId: string): string => `org-person-${personId}`;

/**
 * The hydrator emits generic `org_person` nodes at a 200×120 default that
 * matches no real card variant — wrong shape, and mid-sized enough that the
 * renderer's dimension heuristic reads it as "full", then truncates the
 * field rows it tries to draw. Normalize to the canonical person variant at
 * the dimensions its view mode actually specifies (the same post-hydration
 * step `importOrg25Sample` performs for its photo cards).
 */
export function upgradeSeededPersonNodes<
  T extends { type: string; width?: number; height?: number; w?: number; h?: number },
>(nodes: T[], dimensions: { width: number; height: number }): T[] {
  return nodes.map((node) => {
    if (node.type !== 'org_person') return node;
    return {
      ...node,
      type: DEFAULT_ORG_PERSON_NODE_TYPE,
      width: dimensions.width,
      height: dimensions.height,
      w: dimensions.width,
      h: dimensions.height,
    };
  });
}

/**
 * Pure mapping: permissioned org model → universal org graph of editable
 * person nodes + reporting edges. Only fields the viewer can already see are
 * present in the model, so nothing protected can leak into the Y.Doc.
 */
export function orgModelToUniversalGraph(model: OrgModelProjection): OrgUniversalGraph {
  const hierarchy = buildPeopleHierarchy(model);
  const membership = departmentMembership(model, hierarchy.positionByPersonId);

  const nodes: OrgUniversalGraph['nodes'] = model.people.map((person) => {
    const position = hierarchy.positionByPersonId.get(person.id);
    const title = position?.label
      ?? stringValue(person.props.title)
      ?? stringValue(person.props.role);
    const department = membership.get(person.id)?.label
      ?? stringValue(person.props.department);
    return {
      id: personNodeId(person.id),
      type: 'org_person',
      label: person.label,
      props: {
        personId: person.externalId ?? person.id,
        name: person.label,
        title,
        role: title,
        department,
        photoRef: stringValue(person.props.photoRef),
        email: stringValue(person.props.email),
        // Record binding — survives the hydrator's rest-prop spread into
        // node attrs, which is what OrgChartRecordLinkService keys on.
        folderRecordId: person.id,
      },
    };
  });

  const edges: OrgUniversalGraph['edges'] = hierarchy.edges.map((edge) => ({
    id: `reports-to:${edge.from}:${edge.to}`,
    type: 'reports_to',
    from: personNodeId(edge.from),
    to: personNodeId(edge.to),
    props: {},
  }));

  return { nodes, edges };
}

export async function importOrgFromSource(
  cm: CommandManager,
  doc: Y.Doc | null | undefined,
  folderId: string,
  requestFn: RequestFn,
): Promise<boolean> {
  const [
    { orgChartHydrator },
    { resolveOrgPersonCardDimensions },
    {
      computeAutoCollapseIds,
      applyOrgChartCollapse,
      INITIAL_OVERVIEW_DEPTH,
      INITIAL_OVERVIEW_THRESHOLD,
    },
    { OrgChartImportCommand },
    { LayoutOrgChartCommand },
    { computeFullTreeLayout },
    { useDiagramStore },
    { useOrgChartViewStore },
  ] = await Promise.all([
    import('../interop/OrgChartHydrator'),
    import('../models/orgChartShapeVariants'),
    import('../services/orgChartCollapseProjection'),
    import('../commands/OrgChartImportCommand'),
    import('../commands/LayoutOrgChartCommand'),
    import('../services/graphAnalysis'),
    import('../../../stores/diagramStore'),
    import('../../../stores/orgchartViewStore'),
  ]);

  const response = await requestFn(`/api/folders/${encodeURIComponent(folderId)}/atlas/org-model`);
  if (!response.ok) return false;
  const model = (await response.json()) as OrgModelProjection;
  if (!Array.isArray(model.people) || model.people.length === 0) return false;

  const graph = orgModelToUniversalGraph(model);
  if (graph.nodes.length === 0) return false;

  const scene = orgChartHydrator.fromUniversal(graph);
  const seededNodes = upgradeSeededPersonNodes(
    scene.nodes,
    resolveOrgPersonCardDimensions(DEFAULT_ORG_PERSON_NODE_TYPE, DEFAULT_ORG_CHART_VIEW_MODE),
  );

  const toCollapse = computeAutoCollapseIds(
    seededNodes,
    scene.edges,
    INITIAL_OVERVIEW_THRESHOLD,
    INITIAL_OVERVIEW_DEPTH,
  );
  if (toCollapse.length > 0) {
    useOrgChartViewStore.getState().setCollapsedNodeIds(toCollapse);
  }

  await cm.execute(new OrgChartImportCommand(seededNodes, scene.edges));
  writeDefaultViewMode(doc);

  const latestNodes = useDiagramStore.getState().nodes;
  const latestEdges = useDiagramStore.getState().edges;
  const collapsedIds = useOrgChartViewStore.getState().collapsedNodeIds;
  const collapseResult =
    collapsedIds.length > 0 ? applyOrgChartCollapse(latestNodes, latestEdges, collapsedIds) : null;
  const layoutNodes = collapseResult?.nodes ?? latestNodes;
  const layoutEdges = collapseResult?.edges ?? latestEdges;
  const positions = computeFullTreeLayout(layoutNodes, layoutEdges);
  if (positions.size > 0) {
    await cm.execute(new LayoutOrgChartCommand({ positions, edges: latestEdges, nodes: layoutNodes }));
  }

  useOrgChartViewStore.getState().requestViewportFit();
  // A starter is the canvas's initial state — undoing back to blank is
  // never what the user means (same call the other org starters make).
  cm.clear();
  return true;
}

/**
 * A source import is a whole organization, not a handful of cards — compact
 * is the readable default (and the app's own `DEFAULT_ORG_CHART_VIEW_MODE`).
 * Written explicitly so the diagram carries the decision rather than
 * inheriting it from an absent setting. Users can switch to full cards from
 * Lens & Display; per-card field rows live in the properties panel.
 */
function writeDefaultViewMode(doc: Y.Doc | null | undefined): void {
  if (!doc) return;
  try {
    const map = doc.getMap('diagramSettings');
    const raw = map.get('settings');
    const settings = typeof raw === 'string' ? JSON.parse(raw) : {};
    settings.orgchart = {
      ...(settings.orgchart ?? {}),
      viewMode: DEFAULT_ORG_CHART_VIEW_MODE,
    };
    map.set('settings', JSON.stringify(settings));
  } catch (error) {
    console.warn('[orgFromSourceSeed] failed to write viewMode', error);
  }
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

export const orgFromSourceSeed: SeedProvider = {
  id: 'org-from-source',
  type: 'org',
  label: 'Your organization (from folder data)',
  needsFolderContext: true,
  provide: () => ({
    format: 'commands',
    build: async (cm, ctx) => {
      if (!ctx?.folderId || !ctx.requestFn) {
        console.warn('[orgFromSourceSeed] missing folderId/requestFn context — seeding nothing');
        return;
      }
      await importOrgFromSource(cm, ctx.doc, ctx.folderId, ctx.requestFn);
    },
  }),
};
